import type { ChatAttachment } from "@xop/dashboard-shared";
import { useEffect, useState } from "react";

import { apiBlob } from "../api.js";
import { saveBlob } from "../download.js";

const attachmentPath = (id: string): string => `/api/chat/attachments/${encodeURIComponent(id)}`;

/**
 * Fetch an attachment (bearer-authenticated) and expose it as a local
 * object URL for an `<img>` / `<video>` / `<audio>` — the download endpoint is
 * auth-gated, so a plain `src="/api/..."` can't authenticate. Revokes the URL
 * on unmount / id change so blobs don't leak. Returns null until loaded (or on
 * error).
 *
 * Pass `mime` for media (video/audio): the download endpoint serves non-images
 * as `application/octet-stream`, but `<video>`/`<audio>` decode more reliably
 * when the object-URL blob carries the real media type, so we re-type it.
 */
export function useAttachmentObjectUrl(id: string | null, mime?: string): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!id) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    apiBlob(attachmentPath(id))
      .then((blob) => {
        if (cancelled) return;
        const typed = mime && blob.type !== mime ? blob.slice(0, blob.size, mime) : blob;
        objectUrl = URL.createObjectURL(typed);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [id, mime]);
  return url;
}

/** Download an attachment to disk (bearer-authenticated fetch → shared
 * `saveBlob`: a native Save-As dialog in the desktop app so the user picks
 * the location, browser download on the web). Used for non-image files. */
export async function downloadAttachment(att: ChatAttachment): Promise<void> {
  const blob = await apiBlob(attachmentPath(att.id));
  await saveBlob(att.filename || "file", blob);
}

/** How many bars a voice-note waveform draws. */
export const VOICE_NOTE_PEAK_COUNT = 40;

/**
 * Decode an audio blob to `n` normalized amplitude peaks (0–1) for a WhatsApp
 * waveform. Throws where Web Audio / decoding is unavailable (older WebViews)
 * — the caller falls back to flat bars. The real waveform is computed here,
 * client-side, so the server stores no peaks.
 */
async function computeVoicePeaks(blob: Blob, n: number): Promise<number[]> {
  const Ctx =
    (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
      .AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) throw new Error("no AudioContext");
  const ctx = new Ctx();
  try {
    const audio = await ctx.decodeAudioData(await blob.arrayBuffer());
    const data = audio.getChannelData(0);
    const block = Math.floor(data.length / n) || 1;
    const peaks: number[] = [];
    let max = 0;
    for (let i = 0; i < n; i += 1) {
      let sum = 0;
      const start = i * block;
      for (let j = 0; j < block; j += 1) {
        const v = data[start + j] ?? 0;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / block);
      peaks.push(rms);
      if (rms > max) max = rms;
    }
    return max > 0 ? peaks.map((p) => p / max) : peaks;
  } finally {
    void ctx.close?.();
  }
}

/**
 * Fetch a voice note ONCE (auth-gated blob), exposing both a playable object
 * URL (for a hidden `<audio>`) and the decoded waveform peaks. Peaks are null
 * until decoded, or permanently null where decoding isn't supported (the bubble
 * then draws flat bars). Revokes the URL on unmount / id change.
 */
export function useVoiceNotePlayback(
  id: string,
  mime: string,
): { url: string | null; peaks: number[] | null } {
  const [state, setState] = useState<{ url: string | null; peaks: number[] | null }>({
    url: null,
    peaks: null,
  });
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    apiBlob(attachmentPath(id))
      .then(async (blob) => {
        if (cancelled) return;
        const typed = mime && blob.type !== mime ? blob.slice(0, blob.size, mime) : blob;
        objectUrl = URL.createObjectURL(typed);
        let peaks: number[] | null = null;
        try {
          peaks = await computeVoicePeaks(blob, VOICE_NOTE_PEAK_COUNT);
        } catch {
          peaks = null; // decoding unavailable → flat bars
        }
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setState({ url: objectUrl, peaks });
      })
      .catch(() => {
        if (!cancelled) setState({ url: null, peaks: null });
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setState({ url: null, peaks: null });
    };
  }, [id, mime]);
  return state;
}
